import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import marketingTheme from '../themes/marketingTheme';

const Events = () => {
  const [eventsSignupVisible, setEventsSignupVisible] = useState(false);
  const [webinarSignupVisible, setWebinarSignupVisible] = useState(false);

  // Sample events data - this would typically come from a backend
  const events = [
    {
      name: "Industry 4.0 Workshop",
      description: "Introduction to digital transformation in manufacturing",
      date: "TBA",
      requiresRegistration: true,
    },
    {
      name: "AI in Manufacturing Webinar",
      description: "How artificial intelligence is revolutionizing production",
      date: "TBA",
      requiresRegistration: true,
    },
    {
      name: "Computer Vision Demonstration",
      description: "Live demo of MotionLogic® capabilities",
      date: "TBA",
      requiresRegistration: true,
    },
  ];

  return (
    <Box sx={{ backgroundColor: '#24242a', minHeight: '100vh', color: 'white', pt: 10 }}>
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 'bold',
              mb: 3,
              color: 'white',
            }}
          >
            Stay Informed
          </Typography>
        </Box>

        {/* Upcoming Events Section */}
        <Card
          sx={{
            backgroundColor: '#2d2d33',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            mb: 4,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 'bold',
                mb: 4,
                color: marketingTheme.palette.primary.main,
              }}
            >
              Upcoming Events
            </Typography>

            <TableContainer component={Paper} sx={{ backgroundColor: '#24242a', mb: 4 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Registration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((event, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ color: 'white' }}>{event.name}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{event.description}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{event.date}</TableCell>
                      <TableCell>
                        {event.requiresRegistration && (
                          <Button
                            variant="contained"
                            size="small"
                            sx={{
                              backgroundColor: marketingTheme.palette.primary.main,
                              color: '#000',
                              '&:hover': {
                                backgroundColor: '#d4a028',
                              },
                            }}
                          >
                            Register
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => setEventsSignupVisible(!eventsSignupVisible)}
                sx={{
                  backgroundColor: marketingTheme.palette.primary.main,
                  color: '#000',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#d4a028',
                  },
                }}
              >
                Be Notified for Upcoming Events
              </Button>
              
              <Collapse in={eventsSignupVisible}>
                <Box sx={{ mt: 2, p: 3, backgroundColor: '#24242a', borderRadius: 1 }}>
                  <Typography variant="body1" sx={{ color: 'white', mb: 2 }}>
                    Sign up to receive notifications about upcoming events and workshops.
                  </Typography>
                  {/* This would typically integrate with a form service like Klaviyo */}
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Email signup form would be integrated here.
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          </CardContent>
        </Card>

        {/* Webinars Section */}
        <Card
          sx={{
            backgroundColor: '#2d2d33',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            mb: 4,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 'bold',
                mb: 4,
                color: marketingTheme.palette.primary.main,
              }}
            >
              Webinars
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => setWebinarSignupVisible(!webinarSignupVisible)}
                sx={{
                  backgroundColor: marketingTheme.palette.primary.main,
                  color: '#000',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#d4a028',
                  },
                }}
              >
                Register Interest for Webinar
              </Button>
              
              <Collapse in={webinarSignupVisible}>
                <Box sx={{ mt: 2, p: 3, backgroundColor: '#24242a', borderRadius: 1, width: '100%' }}>
                  <Typography variant="body1" sx={{ color: 'white', mb: 2 }}>
                    Register your interest in our upcoming webinar series covering industrial AI, computer vision, and automation.
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Webinar registration form would be integrated here.
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          </CardContent>
        </Card>

        {/* Lunch and Learn Section */}
        <Card
          sx={{
            backgroundColor: '#2d2d33',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 'bold',
                mb: 4,
                color: marketingTheme.palette.primary.main,
              }}
            >
              Lunch and Learn
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'white',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              Attend a Lunch and Learn session or host one at your workplace. We'll provide expert presentations on industrial AI, automation, and computer vision technologies.
            </Typography>

            <Box
              sx={{
                width: '100%',
                height: '600px',
                backgroundColor: '#24242a',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 4,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textAlign: 'center',
                }}
              >
                Lunch and Learn Registration Form
                <br />
                (Typeform integration would go here)
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      mb: 2,
                      color: 'white',
                    }}
                  >
                    What We Offer
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      lineHeight: 1.6,
                    }}
                  >
                    • 2-hour presentations and networking
                    • Expert insights on Industry 4.0
                    • Live demonstrations of technology
                    • Q&A sessions with our team
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      mb: 2,
                      color: 'white',
                    }}
                  >
                    Requirements
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      lineHeight: 1.6,
                    }}
                  >
                    • Minimum 15 attendees
                    • Maximum 20 attendees
                    • Maximum 5 per organization
                    • Subject to approval
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      mb: 2,
                      color: 'white',
                    }}
                  >
                    Get Started
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      lineHeight: 1.6,
                    }}
                  >
                    Contact us to schedule a session at your workplace or to attend an upcoming public session.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Events;
